// Add / edit vehicle as a real page (/app/fleet/new, /app/fleet/:id/edit) —
// linkable and refreshable, sharing the exact same VehicleForm as before.
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "./_shared";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { VehicleForm } from "./Fleet";

export default function FleetVehicleForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [vehicle, setVehicle] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(!!id);

  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get(`/fleet/${id}`)
      .then(({ data }) => setVehicle(data))
      .catch(() => { toast.error("Vehicle not found"); nav("/app/fleet", { replace: true }); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (user.role === "field") {
    nav("/app/fleet", { replace: true });
    return null;
  }

  return (
    <div data-testid="fleet-vehicle-form-page" className="p-6 lg:p-10 max-w-7xl mx-auto">
      <PageHeader
        tag="/ Vehicles & plant"
        title={id ? "Edit vehicle" : "Add vehicle"}
        subtitle='Track WoF, rego and service expiries. Expiries within 30 days are flagged. Assign an operator so they see "Your vehicle" highlighted.'
        back="/app/fleet"
        action={
          <Button variant="outline" onClick={() => nav("/app/fleet")}
            className="rounded-sm border-zinc-400 uppercase tracking-wider font-bold" data-testid="back-to-fleet">
            <ChevronLeft className="w-4 h-4 mr-1.5" /> Back to fleet
          </Button>
        }
      />
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-zinc-500 py-16" data-testid="vehicle-form-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading vehicle…
        </div>
      ) : (
        <VehicleForm
          variant="page"
          key={vehicle?.id || "new"}
          initial={vehicle}
          users={users}
          onCancel={() => nav("/app/fleet")}
          onDone={() => nav("/app/fleet")}
        />
      )}
    </div>
  );
}
